<script lang="ts" context="module">
  export const lockState = writable({
    previous: false,
    next: false,
    perviousLockMessage: '',
    nextLockMessage: ''
  });

  export function topnavLockPrevious(message: string) {
    lockState.update(state => ({
      ...state,
      previous: true,
      perviousLockMessage: message
    }));
  }
  export function topnavLockNext(message: string) {
    lockState.update(state => ({
      ...state,
      next: true,
      nextLockMessage: message
    }));
  }
  export function topnavUnlockPrevious() {
    lockState.update(state => ({
      ...state,
      previous: false,
      perviousLockMessage: ''
    }));
  }
  export function topnavUnlockNext() {
    lockState.update(state => ({
      ...state,
      next: false,
      nextLockMessage: ''
    }));
  }
</script>

<script lang="ts">
  import {onDestroy, onMount} from 'svelte';
  import {menu, step, draftStore, thumbDraft, project, videos, preview, thumbnail, isMobile} from '@/store';
  import {writable} from 'svelte/store';

  import {MENU, STEP} from '@/types/common';
  import type {Video} from '@/types/project';

  import Swal from 'sweetalert2';
  import {randomString} from '@/utils/common';
  import {tooltip} from '@/actions/tippy-action';
  import {CustomTopNavEvent, TopnavEvents, topnav} from '@/utils/top-nav-next-custom-events';
  import {ThumbDraftService, DraftService, appEvents} from '@/services';

  // Components
  import SaveProjectDialog from '@/pages/choose-video-redesign/Dialogs/SaveProjectDialog.svelte';
  import DownloadPreviewButton from './DownloadPreviewButton.svelte';
  import ThumbnailDownloadButton from './ThumbnailDownloadButton.svelte';
  import Dialog from '@/components/dialog/Dialog.svelte';
  import DraftsModule from '@/components/drafts/DraftsModule.svelte';
  import ThumbDraftsModule from '@/components/thumbnail/ThumbDraftsModule.svelte';
  import {draftName} from '@/store/drafts';
  import {vidDialogOpen} from '@/pages/thumbPreview/viddialog-store';
  import {events} from '@/utils/custom-event-names';

  let videoList: Video[] = [];
  let selectedVideo: Video;
  let isNextDisabled = false;
  let isPreviousDisabled = false;
  let isSaveDialogOpen: boolean = false;
  let isSavingProject: boolean = false;
  let isProject: boolean = false;
  let projectName: string = '';

  let nextButton: HTMLButtonElement;
  let previousButton: HTMLButtonElement;

  let transcriptionProgress = 0;
  let transcriptionSettimeoutId: number | NodeJS.Timeout;

  let isTranscribingDialogOpen = false;

  const maxLoaderTimeout = 11000;
  const minLoaderTimeout = 4000;
  const maxTranscriptionProgress = 80;

  const maxProgressAddition = 25;
  const minProgressAddition = 6;

  const onPrev = () => {
    if ($lockState.previous) {
      Swal.fire({
        title: 'Warning',
        text: $lockState.perviousLockMessage,
        icon: 'warning',
        confirmButtonText: 'Ok'
      });

      return;
    }
    window.dispatchEvent(new Event(isPreviousDisabled ? TopnavEvents.disabledPrevious : TopnavEvents.previous));
  };
  const transcriptionLoadingSimulator = () => {
    if (transcriptionSettimeoutId) clearTimeout(transcriptionSettimeoutId);
    transcriptionSettimeoutId = setTimeout(
      () => {
        if (transcriptionProgress >= maxTranscriptionProgress) return;
        const newProgress =
          transcriptionProgress + Math.random() * (maxProgressAddition - minProgressAddition) + minProgressAddition;
        transcriptionProgress = newProgress > 80 ? 80 : newProgress;
        transcriptionLoadingSimulator();
      },
      Math.random() * (maxLoaderTimeout - minLoaderTimeout) + minLoaderTimeout
    );
  };

  const stopTranscriptionProgressSimulation = (videoid: number | null = null) => {
    if (transcriptionSettimeoutId) clearTimeout(transcriptionSettimeoutId);
    transcriptionProgress = 100;
    project
      .getVideo(videoid ? videoid : undefined)
      .then(({video, error}) => {
        if (!video) throw error;

        videos.update(videos => {
          videos.files = videos.files.map(videoItem => {
            if (videoItem.id === video.id) return video;
            return videoItem;
          });
          return videos;
        });

        isTranscribingDialogOpen = false;
        topnav.dispatchEvent({
          nextDisabled: false,
          nextDisabledTip: ''
        });
      })
      .catch(() => {
        isTranscribingDialogOpen = false;
        topnav.dispatchEvent({
          nextDisabled: false,
          nextDisabledTip: ''
        });
        Swal.fire({
          position: 'bottom-end',
          icon: 'error',
          title: 'Could not load transcription',
          showConfirmButton: false,
          timer: 1000,
          toast: true
        });
      });
  };

  const onVideoChange = async (projectVideo: Video) => {
    if (transcriptionSettimeoutId) clearTimeout(transcriptionSettimeoutId);
    transcriptionProgress = 0;
    isTranscribingDialogOpen = false;

    const {transcription} = projectVideo;
    if (transcription && transcription.transcription !== null && transcription.operationId === null) return;

    isTranscribingDialogOpen = true;
    topnav.dispatchEvent({
      nextDisabled: true,
      nextDisabledTip: 'Transcription is still processing'
    });

    transcriptionLoadingSimulator();
  };

  const draftService = new DraftService();
  const thumbDraftService = new ThumbDraftService();

  // this function is shared that's why it's here
  const saveNewProject = async () => {
    try {
      const result = {value: projectName};

      if (result.value == '') {
        return false;
      }

      if (typeof $draftStore.currentCloudDraftId !== 'number' || $draftStore.currentCloudDraftId <= 0) {
        return false;
      }

      await draftService.makeProject($draftStore.currentCloudDraftId as number, result.value);
      draftStore.markDraftAsProject(result.value);

      return true;
    } catch (error) {
      console.error('save project error', error);
      Swal.fire({
        position: 'top-end',
        icon: 'warning',
        title: 'Project not saved',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });
      return false;
    }
  };

  const saveThumbProject = async () => {
    if ($thumbDraft.isProject) {
      // console.log("saveNewProject", $thumbDraft.currentCloudDraftId)
      let drafftResult = false;
      try {
        drafftResult = await thumbDraftService.rename(String($thumbDraft.currentCloudDraftId), {
          projectName: projectName
        });
      } catch (error) {
        drafftResult = false;
        console.error('save project error', error);
      }

      Swal.fire({
        position: 'top-end',
        icon: !drafftResult ? 'error' : 'success',
        title: !drafftResult ? 'Failed to update project' : 'Project updated',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });

      return drafftResult;
    }

    try {
      const result = {value: projectName};

      if (result.value == '') {
        return false;
      }

      if (typeof $thumbDraft.currentCloudDraftId !== 'number' || $thumbDraft.currentCloudDraftId <= 0) {
        return false;
      }

      await thumbDraftService.makeProject($thumbDraft.currentCloudDraftId as number, result.value);
      thumbDraft.markDraftAsProject(result.value);

      return true;
    } catch (error) {
      console.error('save project error', error);
      Swal.fire({
        position: 'top-end',
        icon: 'warning',
        title: 'Project not saved',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });
      return false;
    }
  };

  function back2Step1() {
    step.update(STEP.CHOOSE_VIDEO);
    vidDialogOpen.set(true);
  }

  function saveProject() {
    isSaveDialogOpen = true;
  }

  const projectSubscription = project.subscribe(value => {
    const projectVideo = value.video;
    if (!projectVideo) return;
    selectedVideo = projectVideo;
    onVideoChange(projectVideo);
  });

  const thumbnailSubscription = thumbnail.subscribe(value => {
    const projectVideo = value.video;
    if (!projectVideo) return;
    selectedVideo = projectVideo;
    onVideoChange(projectVideo);
  });

  const videosSubscription = videos.subscribe(value => {
    videoList = value.files;
  });

  let nextTippyInstance: any;
  let previousTippyInstance: any;

  const TopnavCustomEvent = (event: any) => {
    if (typeof event.detail !== 'object') return;
    const details = (event.detail || {}) as CustomTopNavEvent;

    isNextDisabled = details.nextDisabled ? true : false;
    isPreviousDisabled = details.previousDisabled ? true : false;

    if (nextTippyInstance && nextTippyInstance.destroy) {
      try {
        nextTippyInstance.destroy();
        nextTippyInstance = null;
      } catch (err) {
        console.warn('could not destroy tippy instance [next]', err);
      }
    }
    if (previousTippyInstance && previousTippyInstance.destroy) {
      try {
        previousTippyInstance.destroy();
        previousTippyInstance = null;
      } catch (err) {
        console.warn('Could not destroy tippy instance [previous]', err);
      }
    }

    if (typeof details.nextDisabledTip === 'string' && details.nextDisabledTip !== '' && isNextDisabled) {
      nextTippyInstance = tooltip(nextButton, details.nextDisabledTip);
    }

    if (typeof details.previousDisabledTip === 'string' && details.previousDisabledTip !== '' && isPreviousDisabled) {
      previousTippyInstance = tooltip(previousButton, details.previousDisabledTip);
    }
  };

  window.addEventListener(TopnavEvents.customEvent, TopnavCustomEvent);

  function transcriptionComplete(data: any) {
    stopTranscriptionProgressSimulation(data?.identifier || null);
    isTranscribingDialogOpen = false;
  }

  function transcriptionError() {
    stopTranscriptionProgressSimulation();
    Swal.fire({
      title: 'There was a problem with the transcription',
      text: 'There may have been a problem with the transcription. Please try again or select a different video',
      icon: 'error'
    });
    step.update(STEP.CHOOSE_VIDEO);
    isTranscribingDialogOpen = false;
  }

  const unsubscribeDrafts = draftStore.subscribe(draft => {
    if ($menu === MENU.CLIPS) {
      projectName = (draft.projectName as string)
        ? (draft.projectName as string)
        : `${$project.video?.title || 'Untitled Project'}`;
      if (!draft.isSaving) {
        draftName.set(projectName);
      }
      isProject = draft.isProject as boolean;
      isSavingProject = draft.isSaving;
    }
  });

  const unSubThumbDrafts = thumbDraft.subscribe(draft => {
    if ($menu === MENU.THUMBNAILS) {
      projectName = (draft.projectName as string)
        ? (draft.projectName as string)
        : `${$thumbnail.video?.title || 'Untitled Project'}`;
      if (!draft.isSaving) {
        draftName.set(projectName);
      }
      isProject = draft.isProject as boolean;
      isSavingProject = draft.isSaving;
    }
  });

  let steps: {id: number; label: string; key: string}[] = [];

  if ($menu === MENU.CLIPS) {
    steps = [
      {id: 1, label: 'Select a template', key: 'CHOOSE_TEMPLATE'},
      {id: 2, label: 'Select Subtitles', key: 'SELECT_SUBTITLES'},
      {id: 3, label: 'Clip Preview', key: 'PREVIEW'}
    ];
  } else if ($menu === MENU.THUMBNAILS) {
    steps = [
      {id: 1, label: 'Select a template', key: 'CHOOSE_TEMPLATE'},
      {id: 2, label: 'Image Preview', key: 'PREVIEW'}
    ];
  }

  async function renameVideo() {
    if (!selectedVideo) return console.error('selectedVideo is null');
  }

  function formatDuration(duration: number) {
    if (typeof duration !== 'number' || duration <= 0) return '00:00:00.00';

    const capturedMinutes = duration / 60;

    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (capturedMinutes >= 60) {
      minutes = capturedMinutes % 60;
      hours = (capturedMinutes - minutes) / 60;
    } else {
      minutes = capturedMinutes;
    }

    const intMinutes = parseInt(minutes.toString());

    seconds = (minutes - intMinutes) * 60;
    minutes = intMinutes;

    return `${hours > 99 ? '+99' : hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toFixed(0)
      .padStart(2, '0')}`;
  }

  onMount(() => {
    appEvents.on('transcription-completed', transcriptionComplete);
    appEvents.on('transcription-error', transcriptionError);
  });

  onDestroy(() => {
    appEvents.off('transcription-completed', transcriptionComplete);
    appEvents.off('transcription-error', transcriptionError);
    //stopTranscriptionProgressSimulation();

    projectSubscription();
    thumbnailSubscription();
    videosSubscription();
    unsubscribeDrafts();

    window.removeEventListener(TopnavEvents.customEvent, TopnavCustomEvent);
    unSubThumbDrafts();
  });
</script>

<div class="flex h-full w-full flex-col gap-4 px-4 lg:flex-row">
  {#if $step !== STEP.ADMIN && $step !== STEP.ANALYTICS}
    {#if !$isMobile}
      <i class="fa fa-angle-right mx-4 my-auto text-base text-gray-200" aria-hidden="true" />
    {/if}
    <div class="flex h-full items-center gap-4 py-2 {$isMobile ? 'flex-col' : 'flex-row'}">
      {#if $isMobile}
        <div class="flex w-full flex-row justify-between gap-4">
          <div class="flex w-full flex-row gap-8">
            <i
              on:click={() => {
                window.dispatchEvent(new Event(events.openSideNav));
              }}
              on:keypress={() => {
                window.dispatchEvent(new Event(events.openSideNav));
              }}
              class="fa-solid fa-bars cursor-pointer text-2xl text-zinc-600"
            />
            <div class="flex flex-row space-y-2">
              <!-- Title Dropdown -->
              <div
                class="flex cursor-pointer items-center space-x-3"
                use:tooltip={'Open features'}
                on:click={() => vidDialogOpen.set(true)}
                on:keydown={() => vidDialogOpen.set(true)}
              >
                <span class="font-semibold text-dark"> {$menu.replace(/_/g, ' ')}</span>
                <i class="fa-solid fa-angles-up-down text-sm text-gray-400" />
              </div>
            </div>
          </div>
          <i class="fa-solid fa-xmark" on:click={back2Step1} on:keypress={back2Step1} />
        </div>
        <div class="flex w-full flex-row items-center gap-6">
          <img
            use:tooltip={'Back to project'}
            on:click={() => back2Step1()}
            on:keydown={() => back2Step1()}
            class="h-16 shrink-0 cursor-pointer rounded-lg lg:h-[50px]"
            src={selectedVideo?.preview}
            alt="selected-video-thumbnail"
          />
          <input
            type="text"
            class="h-10 min-w-[200px] rounded-md border bg-gray-50 px-3 py-2 font-semibold text-gray-800"
            bind:value={$draftName}
          />
        </div>
      {:else}
        <img
          use:tooltip={'Back to project'}
          on:click={() => back2Step1()}
          on:keydown={() => back2Step1()}
          class="h-10 shrink-0 cursor-pointer rounded-lg lg:h-[50px]"
          src={selectedVideo?.preview}
          alt="selected-video-thumbnail"
        />
      {/if}
      <!-- {#if $isMobile}
		 <div class="flex flex-col justify-center px-2 h-full text-xs">
            <div class="w-full flex gap-2 min-w-0">
                <button on:click={() => back2Step1()} class="flex items-center gap-1 py-1" title="Start over">
                    <i class="fa-solid fa-rotate-right"></i>
                    <h1 class="truncate max-w-[200px]">{ selectedVideo?.title || 'No Title' }</h1>
                </button>
                <div class="flex items-center">
                    <i class="fa-solid fa-angle-right"></i>
                </div>
                <div class="flex items-center min-w-0">
                    <h1 class="truncate max-w-[240px]">{ projectName }</h1>
                </div>
            </div>
			<div class="inline-block py-1">
				<p
					class="px-2 p-0.5 text-xs text-gray-700 text-center bg-gray-100 dark:bg-zinc-600 rounded-lg w-auto"
				>
					Selected
				</p>
			</div>
			<h1 class="flex gap-2 text-lg font-bold text-black py-1">
				{selectedVideo?.title}
				<button on:click={() => renameVideo()}><i class="fa-solid fa-pen-to-square"></i></button>
			</h1>
			<p class="text-base text-gray-500 dark:text-zinc-400">
				Duration: {formatDuration(
					selectedVideo?.metadata?.duration || 0,
				)}
			</p>
		</div> 
        {/if} -->
      {#if !$isMobile}
        <i class="fa fa-angle-right mx-4 text-base text-gray-200" aria-hidden="true" />
        <div class="flex flex-col space-y-2">
          <!-- Title Dropdown -->
          <div
            class="flex cursor-pointer items-center space-x-3"
            use:tooltip={'Open features'}
            on:click={() => vidDialogOpen.set(true)}
            on:keydown={() => vidDialogOpen.set(true)}
          >
            <span class="font-semibold text-gray-500"> {$menu.replace(/_/g, ' ')}</span>
            <i class="fa-solid fa-angles-up-down text-sm text-gray-400" />
          </div>
          <div class="flex flex-row items-center justify-center space-x-1">
            {#each steps as s, index}
              <!-- Step -->
              <div class="flex items-center">
                <div
                  class="flex h-6 w-6 items-center justify-center rounded-full font-bold {$step === s.key
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-500'}"
                >
                  {s.id}
                </div>
                {#if $step === s.key}
                  <span class="ml-2 font-semibold {$step === s.key ? 'text-gray-900' : 'text-gray-500'}">
                    {s.label}
                  </span>
                {/if}
              </div>

              <!-- Line (skip after the last step) -->
              {#if index < steps.length - 1}
                <div class="h-1 w-6 bg-gray-300" />
              {/if}
            {/each}
          </div>
        </div>
        <!-- Arrow -->
        <i class="fa fa-angle-right mx-4 text-base text-gray-200" aria-hidden="true" />
        <!-- Project Title -->
        <input
          type="text"
          class="min-w-[200px] rounded-md border bg-gray-50 px-3 py-2 font-semibold text-gray-800"
          bind:value={$draftName}
        />
      {/if}
    </div>
  {/if}
  <div class="relative ml-auto flex w-full shrink-0 gap-2 lg:w-auto {$isMobile ? 'pr-2' : ''}">
    {#if !$isMobile}
      <button
        on:click={() => saveProject()}
        class="flex items-center gap-2 rounded-lg border-zinc-300 px-4 py-2 text-zinc-700 transition-all hover:bg-zinc-200 hover:text-zinc-800"
      >
        Save Changes
      </button>
      <button
        bind:this={previousButton}
        on:click={onPrev}
        class="{isPreviousDisabled
          ? 'border-zinc-200 text-zinc-300'
          : 'border-zinc-300 text-zinc-700 hover:bg-zinc-200 hover:text-zinc-800'} flex items-center gap-2 rounded-lg border px-4 py-2 transition-all"
      >
        <i class="fa-solid fa-chevron-left" />
        Previous
      </button>
    {/if}
    {#if $step === STEP.PREVIEW && $menu === MENU.CLIPS}
      <DownloadPreviewButton {saveNewProject} />
    {:else if $step === STEP.PREVIEW && $menu === MENU.THUMBNAILS}
      <ThumbnailDownloadButton saveNewProject={saveThumbProject} />
    {:else}
      <div class="relative">
        <div
          class="{isNextDisabled
            ? 'opacity-0'
            : 'pingAnim'} flex h-full items-center gap-2 rounded-lg border border-rose-500 bg-rose-500 px-4 py-2 text-white transition-all hover:bg-rose-600"
        >
          Next
          <i class="fa-solid fa-chevron-right" />
        </div>
        <button
          bind:this={nextButton}
          on:click={() =>
            window.dispatchEvent(new Event(isNextDisabled ? TopnavEvents.disabledNext : TopnavEvents.next))}
          class="absolute left-0 top-0 {isNextDisabled
            ? 'border-zinc-300 text-zinc-400'
            : 'border-rose-500 bg-rose-500 text-white hover:bg-rose-600'} flex h-full items-center gap-2 rounded-lg border px-4 py-2 transition-all"
        >
          Next
          <i class="fa-solid fa-chevron-right" />
        </button>
      </div>
    {/if}
  </div>
</div>

{#if $menu === MENU.CLIPS}
  <DraftsModule />
{:else if $menu === MENU.THUMBNAILS}
  <ThumbDraftsModule />
{/if}

<Dialog isDialogOpen={isTranscribingDialogOpen} useIfShow={true}>
  <div class="pointer-events-none flex h-full w-full items-center justify-center">
    <div
      class="pointer-events-auto mx-4 h-auto w-full min-w-[280px] max-w-[400px] rounded-2xl border border-gray-500 bg-white shadow-lg dark:border-zinc-600 dark:bg-zinc-700"
    >
      <div class="flex flex-col justify-center p-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 py-1.5">
            <h1 class="text-xl font-bold">Transcribing...</h1>
          </div>
          <button
            type="button"
            on:click={() => (isTranscribingDialogOpen = false)}
            class="my-2 ml-auto rounded-lg px-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
          >
            <i class="fa-solid fa-xmark text-2xl" />
          </button>
        </div>
        <hr class="border-t border-t-gray-300 dark:border-zinc-600" />
        <div class="grid w-full gap-1 pt-4">
          <div class="flex w-full justify-between text-xs text-zinc-700">
            <h1>Progress</h1>
            <h1>{transcriptionProgress?.toFixed(0)}%</h1>
          </div>
          <div class="relative relative h-2 w-full overflow-hidden rounded-lg bg-zinc-300">
            <div class="bg-primary" style="width: {transcriptionProgress}%">&nbsp;</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</Dialog>
<SaveProjectDialog
  bind:isSaveDialogOpen
  bind:isProject
  bind:isSavingProject
  draft={$menu === MENU.CLIPS ? $draftStore : $thumbDraft}
  {projectName}
/>

<style>
  .pingAnim {
    animation: pinganimation 500ms forwards;
  }

  @keyframes pinganimation {
    0% {
      scale: 100%;
      opacity: 100%;
    }
    100% {
      scale: 135%;
      opacity: 0%;
    }
  }
</style>
